import type { Scale } from '@tunasong/schemas'
import type { Chord, ChordVariant } from '../chord/index.js'
import { isChordNotes } from '../chord/chord-types.js'
import { getNoteChromaticDistance } from '../note/note-lib.js'
import type { ChordBorrowing, ChordFunction } from './types.js'

/**
 * Map from semitones-above-tonic to the secondary-dominant label, in MAJOR keys.
 *
 * The dominant of any chord is a perfect fifth above its target. Mapping
 * the targets we support to their dominants in C major:
 *
 *   target ii (D)  → V/ii is A  → A is 9 semitones above C
 *   target IV (F)  → V/IV is C  → 0 (shares root with tonic — see below)
 *   target V  (G)  → V/V  is D  → 2
 *   target vi (A)  → V/vi is E  → 4
 *
 * V/IV (C7 in C major) shares its root with the tonic. We disambiguate by
 * chord-quality: V/IV must be a dominant-7 chord. We require that across all
 * secondary dominants in v1 (a plain triad is not a "secondary dominant" in
 * the analytical sense — it's just a major chord that happens to be a fifth
 * above a target).
 */
const SEC_DOM_BY_SEMITONES_MAJOR: Record<number, ChordFunction> = {
  9: 'V/ii',
  0: 'V/IV',
  2: 'V/V',
  4: 'V/vi',
}

export const getSecondaryDominantFunction = (
  chord: Chord,
  scale: Scale
): { chordFunction: ChordFunction; borrowing: ChordBorrowing } | null => {
  if (scale.type !== 'major') {
    // v1 supports secondary dominants in major keys only.
    return null
  }
  if (!isDominantSeventh(chord)) {
    return null
  }
  const semitones = getNoteChromaticDistance(scale.root, chord.root)
  const fn = SEC_DOM_BY_SEMITONES_MAJOR[semitones]
  if (!fn) {
    return null
  }
  return { chordFunction: fn, borrowing: 'secondary-dominant' }
}

/**
 * Is the chord a dominant-7 chord (e.g. G7, A7)?
 *
 * Confirmed against lib/music-lib/src/chord/chord-types.ts: chord quality lives
 * on `chord.variant`, and the literal for a plain dominant-7 is `'7'` (e.g.
 * `A7` parses to `{ root: 'A', variant: '7' }`). Other dominant variants like
 * '7b5', '7#5', '7#9', '7b9', '7sus2', '7sus4' are intentionally excluded from
 * v1 — extensions/alterations are out of scope for the analyzer's first pass.
 *
 * Type-predicate form so callers narrow to `ChordVariant` (the branch with
 * `.root`); `Chord` is a union that includes `ChordNotes` which lacks `.root`.
 */
function isDominantSeventh(chord: Chord): chord is ChordVariant {
  if (isChordNotes(chord)) {
    return false
  }
  return chord.variant === '7'
}
